import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Color, EMPLOYMENT_RULES_ROUTE, FIRST_HIRE_STEPS, FIRST_HIRE_STEPS_PATH, JOB_ROLES_ROUTE, RECORD_ROUTES, Size, firstHireReady, firstHireSteps, t,
  type FirstHireStep, type FirstHireStepsView, type StringKey,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { SetupStep, SetupSteps, type SetupStepState } from '../../components/composites/SetupSteps';
import { useApi } from '../records/api';
import { ORGANIZATION_PROFILE_ROUTE } from '../settings/retiredRoutes';
import { line } from '../settings/SettingsShell';
import styles from './HrPages.module.css';

const TITLE: Readonly<Record<FirstHireStep, StringKey>> = {
  domain: 'hr.firstHire.domain.title',
  site: 'hr.firstHire.site.title',
  jobRole: 'hr.firstHire.jobRole.title',
  rules: 'hr.firstHire.rules.title',
};

const BODY: Readonly<Record<FirstHireStep, StringKey>> = {
  domain: 'hr.firstHire.domain.body',
  site: 'hr.firstHire.site.body',
  jobRole: 'hr.firstHire.jobRole.body',
  rules: 'hr.firstHire.rules.body',
};

const OPEN: Readonly<Record<FirstHireStep, StringKey>> = {
  domain: 'hr.firstHire.domain.open',
  site: 'hr.firstHire.site.open',
  jobRole: 'hr.firstHire.jobRole.open',
  rules: 'hr.firstHire.rules.open',
};

const stateOf = (done: boolean, reachable: boolean): SetupStepState => (done ? 'done' : reachable ? 'current' : 'later');

/** Where each step is done; our own Company's page is read off the answer. */
function linkOf(step: FirstHireStep, facts: FirstHireStepsView): string {
  switch (step) {
    case 'domain': return `${ORGANIZATION_PROFILE_ROUTE}#email-domains`;
    case 'site': return facts.ownCompanyId ? RECORD_ROUTES.CompanyRecord.record.replace(':id', encodeURIComponent(facts.ownCompanyId)) : RECORD_ROUTES.CompanyRecord.list;
    case 'jobRole': return JOB_ROLES_ROUTE;
    default: return EMPLOYMENT_RULES_ROUTE;
  }
}

/**
 * The First hire guide above the Hire window's sections (piece 34; decision
 * 6842), drawn through Setup Steps (decision 1647). 🔒 Each step's state is read
 * from the server's answer and never stored; the guide draws while any step
 * stands undone and is gone once all four are done. 🔨 A step links the page
 * that does it and writes nothing.
 */
export function FirstHireSteps({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const read = useApi<FirstHireStepsView>(FIRST_HIRE_STEPS_PATH, apiBaseUrl);
  const navigate = useNavigate();
  const [opened, setOpened] = useState<ReadonlySet<FirstHireStep>>(new Set());
  const toggle = (step: FirstHireStep) => setOpened((was) => {
    const next = new Set(was);
    if (next.has(step)) next.delete(step);
    else next.add(step);
    return next;
  });
  // An answer that is missing or failed draws nothing rather than throwing (the fixture rule of CLAUDE.md).
  if (read.state.kind !== 'ready' || !read.state.data) return null;
  const facts = read.state.data;
  if (firstHireReady(facts)) return null;
  const states = firstHireSteps(facts);

  /** What each step found, in a line; a done step's summary is the same line. */
  const found = (step: FirstHireStep): string => {
    switch (step) {
      case 'domain': return facts.domain ? line(t('hr.firstHire.domain.done'), facts.domain) : t('hr.firstHire.domain.none');
      case 'site': return facts.ownSites > 0 ? line(t('hr.firstHire.site.done'), String(facts.ownSites)) : t('hr.firstHire.site.none');
      case 'jobRole': return facts.activeJobRoles > 0 ? line(t('hr.firstHire.jobRole.done'), String(facts.activeJobRoles)) : t('hr.firstHire.jobRole.none');
      default: {
        if (facts.ownSites === 0) return t('hr.firstHire.waitsSite');
        const missing = facts.statesWithoutRules ?? [];
        const parts = [missing.length > 0 ? line(t('hr.firstHire.rules.missing'), missing.join(', ')) : null, (facts.sitesWithoutState ?? 0) > 0 ? t('hr.firstHire.rules.noState') : null].filter((part): part is string => part !== null);
        return parts.length > 0 ? parts.join(' ') : t('hr.firstHire.rules.done');
      }
    }
  };

  return (
    <div className={styles.section} data-first-hire="steps">
      <SetupSteps guide="firstHire" heading={t('hr.firstHire.title')} headingClassName={styles.heading} intro={<p className={styles.meta}>{t('hr.firstHire.hint')}</p>} dataAttribute={{ 'data-first-hire-steps': 'ready' }}>
        {FIRST_HIRE_STEPS.map((step, index) => {
          const state = stateOf(states[step].done, states[step].reachable);
          const hold = state === 'later' ? t('hr.firstHire.waitsSite') : null;
          return (
            <SetupStep
              key={step}
              hook="first-hire"
              number={index + 1}
              name={step}
              title={t(TITLE[step])}
              state={state}
              open={opened.has(step)}
              onToggle={() => toggle(step)}
              summary={<p className={styles.meta}>{found(step)}</p>}
              holdReason={hold}
            >
              <p className={styles.meta} data-first-hire-found={step}>{found(step)}</p>
              <p className={styles.meta}>{t(BODY[step])}</p>
              <div className={styles.controls} data-first-hire-open={step}>
                <Button size={Size.Medium} color={Color.Navy} text={[t(OPEN[step])]} actions={[() => navigate(linkOf(step, facts))]} disabled={state === 'later'} disabledReason={hold ?? ''} />
              </div>
            </SetupStep>
          );
        })}
      </SetupSteps>
    </div>
  );
}
